import { useCallback, useEffect, useRef, useState } from "react";
import React from "react";
import {
    ORGANIZATION_SEARCH_DEBOUNCE_MS,
    ORGANIZATION_SEARCH_MIN_CHARACTERS,
    ORGANIZATION_SEARCH_PAGE_SIZE,
} from "../constants";
import {
    getOrganizationById,
    getOrganizationOptions,
} from "../hooks/useOrganizations";
import type { OrganizationRecord } from "../types/index";

type Props = {
    changeOrganization: (org: any) => void | Promise<void>;
    setShowOrgMenu: (show: boolean) => void;
    organizationId: number;
    isSuperUser: boolean;
    defaultOrganizationId?: number | null;
    defaultOrganizationName?: string | null;
    previousOrganization?: OrganizationRecord | null;
};

interface IStateData {
    searchTerm: string;
    results: IPagedResult;
    isLoading: boolean;
    loadError: string | null;
    data: OrganizationRecord[];
}

interface IPagedResult {
    page: number;
    pageSize: number;
    total: number;
}

const OrganizationSearcher = ({
    changeOrganization,
    setShowOrgMenu,
    organizationId,
    defaultOrganizationId = null,
    defaultOrganizationName = null,
    previousOrganization = null,
}: Props) => {
    const organizationSearchParam = new URLSearchParams(window.location.search).get('organization');
    const [state, setState] = useState<IStateData>({
        searchTerm: "",
        data: [],
        results: {
            page: 0,
            pageSize: ORGANIZATION_SEARCH_PAGE_SIZE,
            total: 0,
        },
        isLoading: false,
        loadError: null,
    });

    const inputRef = useRef<HTMLInputElement>(null);
    const resultsRef = useRef<HTMLDivElement | null>(null);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const requestIdRef = useRef(0);

    const includePinnedOrganizations = useCallback(async (
        organizations: OrganizationRecord[],
        maxResults?: number,
    ) => {
        const pinnedOrganizations: OrganizationRecord[] = [];
        const addPinnedOrganization = (organization?: OrganizationRecord | null) => {
            if (!organization?.id) return;
            if (!pinnedOrganizations.some((item) => String(item.id) === String(organization.id))) {
                pinnedOrganizations.push(organization);
            }
        };
        const findByName = (name: string | null | undefined) => {
            const normalizedName = String(name || '').trim().toLowerCase();
            if (!normalizedName) return null;
            return organizations.find(
                (item) => String(item?.name || '').trim().toLowerCase() === normalizedName,
            ) || null;
        };
        const findById = (id: number | null | undefined) => (
            id ? organizations.find((item) => String(item?.id) === String(id)) || null : null
        );

        if (previousOrganization && String(previousOrganization.id) !== String(organizationId)) {
            const existingPreviousOrganization = findById(previousOrganization.id);
            if (existingPreviousOrganization) {
                addPinnedOrganization(existingPreviousOrganization);
            } else {
                try {
                    addPinnedOrganization(await getOrganizationById(previousOrganization.id));
                } catch (error) {
                    console.error('Failed to load previous organization:', error);
                }
            }
        }

        const existingRequestedOrganization = findByName(organizationSearchParam);
        if (existingRequestedOrganization) {
            addPinnedOrganization(existingRequestedOrganization);
        } else if (organizationSearchParam?.trim()) {
            try {
                const response = await getOrganizationOptions({
                    name: organizationSearchParam,
                    page: 0,
                    pageSize: ORGANIZATION_SEARCH_PAGE_SIZE,
                });
                addPinnedOrganization(response.data.find((item) =>
                    String(item.name).trim().toLowerCase() === organizationSearchParam.trim().toLowerCase(),
                ));
            } catch (error) {
                console.error('Failed to load requested organization:', error);
            }
        }

        const existingDefaultOrganization =
            findById(defaultOrganizationId) || findByName(defaultOrganizationName);
        if (existingDefaultOrganization) {
            addPinnedOrganization(existingDefaultOrganization);
        } else if (defaultOrganizationId || defaultOrganizationName?.trim()) {
            try {
                const defaultOrganization = defaultOrganizationId
                    ? await getOrganizationById(defaultOrganizationId)
                    : (await getOrganizationOptions({
                        name: defaultOrganizationName,
                        page: 0,
                        pageSize: ORGANIZATION_SEARCH_PAGE_SIZE,
                    })).data.find(
                        (item) => String(item.name).trim().toLowerCase() === defaultOrganizationName?.trim().toLowerCase(),
                    );
                addPinnedOrganization(defaultOrganization);
            } catch (error) {
                console.error('Failed to load default organization:', error);
            }
        }

        const defaultOrganization = pinnedOrganizations.find((organization) =>
            String(organization.id) === String(defaultOrganizationId) ||
            String(organization.name || '').trim().toLowerCase() ===
                String(defaultOrganizationName || '').trim().toLowerCase(),
        ) || null;
        const orderedPinnedOrganizations = [
            ...(defaultOrganization ? [defaultOrganization] : []),
            ...pinnedOrganizations.filter((organization) => organization !== defaultOrganization),
        ];
        const mergedOrganizations = [...orderedPinnedOrganizations, ...organizations].filter(
            (organization, index, items) =>
                items.findIndex((item) => String(item.id) === String(organization.id)) === index,
        );
        return typeof maxResults === 'number'
            ? mergedOrganizations.slice(0, maxResults)
            : mergedOrganizations;
    }, [
        defaultOrganizationId,
        defaultOrganizationName,
        organizationId,
        organizationSearchParam,
        previousOrganization,
    ]);

    useEffect(() => {
        const frameId = window.requestAnimationFrame(() => {
            inputRef.current?.focus();
        });

        return () => {
            window.cancelAnimationFrame(frameId);
        };
    }, []);

    /* Load the first lightweight page when the menu opens. */
    useEffect(() => {
        if (state.searchTerm.length >= ORGANIZATION_SEARCH_MIN_CHARACTERS) return;

        const loadInitialOrgs = async () => {
            const requestId = ++requestIdRef.current;
            setState((prev) => ({ ...prev, isLoading: true, loadError: null }));
            try {
                const response = await getOrganizationOptions({
                    name: null,
                    page: 0,
                    pageSize: ORGANIZATION_SEARCH_PAGE_SIZE,
                });
                if (requestId !== requestIdRef.current) return;
                const orgs = await includePinnedOrganizations(response.data || []);
                if (requestId !== requestIdRef.current) return;
                setState((prev) => ({
                    ...prev,
                    isLoading: false,
                    loadError: null,
                    data: orgs,
                    results: {
                        page: 0,
                        pageSize: ORGANIZATION_SEARCH_PAGE_SIZE,
                        total: response.total,
                    },
                }));
            } catch (error) {
                if (requestId !== requestIdRef.current) return;
                console.error("Failed to load initial organizations:", error);
                setState((prev) => ({ ...prev, isLoading: false, loadError: 'Failed to load organizations.' }));
            }
        };

        loadInitialOrgs().catch(() => null);
    }, [includePinnedOrganizations, state.searchTerm]);

    /* =========================
       SEARCH EFFECT
    ==========================*/
    useEffect(() => {
        if (state.searchTerm.length < ORGANIZATION_SEARCH_MIN_CHARACTERS) {
            return;
        }
        requestIdRef.current += 1;

        // Reset page + data on new search
        setState((prev) => ({
            ...prev,
            data: [],
            results: {
                ...prev.results,
                page: 0,
            },
        }));
    }, [state.searchTerm]);

    /* =========================
       FETCH EFFECT (page/search change)
    ==========================*/
    useEffect(() => {
        if (state.searchTerm.length < ORGANIZATION_SEARCH_MIN_CHARACTERS && state.results.page === 0) return;

        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        debounceTimer.current = setTimeout(fetchOrganizations, ORGANIZATION_SEARCH_DEBOUNCE_MS);

        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [state.results.page, state.searchTerm]);

    const fetchOrganizations = async () => {
        const requestId = ++requestIdRef.current;
        const { page } = state.results;
        const searchTerm = state.searchTerm;
        setState((prev) => ({ ...prev, isLoading: true, loadError: null }));

        try {
            const response = await getOrganizationOptions({
                name: searchTerm || null,
                page,
                pageSize: ORGANIZATION_SEARCH_PAGE_SIZE
            });
            if (requestId !== requestIdRef.current) return;
            const organizations = await includePinnedOrganizations(response.data || []);
            if (requestId !== requestIdRef.current) return;

            setState((prev) => ({
                ...prev,
                isLoading: false,
                loadError: null,
                results: {
                    ...prev.results,
                    total: response.total,
                },
                data:
                    page === 0
                        ? organizations
                        : [...prev.data, ...organizations].filter(
                            (organization, index, items) =>
                                items.findIndex((item) => String(item.id) === String(organization.id)) === index,
                        ),
            }));
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            console.error("Failed to fetch organizations:", error);
            cleanState('Failed to load organizations.');
        }
    };

    /* =========================
       SCROLL PAGINATION
    ==========================*/
    useEffect(() => {
        const element = resultsRef.current;
        if (!element) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = element;

            if (
                !state.isLoading &&
                scrollTop + clientHeight >= scrollHeight - 5
            ) {
                const { total, pageSize, page } = state.results;
                const totalPages = Math.ceil(total / pageSize);

                if (page + 1 < totalPages) {
                    setState((prev) => ({
                        ...prev,
                        results: {
                            ...prev.results,
                            page: prev.results.page + 1,
                        },
                        isLoading: true,
                    }));
                }
            }
        };

        element.addEventListener("scroll", handleScroll);

        return () => {
            element.removeEventListener("scroll", handleScroll);
        };
    }, [state.isLoading, state.results]);

    const cleanState = (loadError: string | null = null) => {
        setState((prev) => ({
            ...prev,
            isLoading: false,
            loadError,
            data: [],
            results: {
                page: 0,
                pageSize: ORGANIZATION_SEARCH_PAGE_SIZE,
                total: 0,
            },
        }));
    };

    useEffect(() => () => {
        requestIdRef.current += 1;
    }, []);

    //const sortedOrgs = [...state.data].sort((a, b) =>
    //    a.name.localeCompare(b.name)
    //);

    return (
        <div className="org-menu" data-testid="organization-search">
            <input
                ref={inputRef}
                type="text"
                value={state.searchTerm}
                className={`input filter-search ${state.isLoading ? 'loading' : ''}`}
                placeholder="Search organizations"
                onChange={(e) =>
                    setState((prev) => ({
                        ...prev,
                        searchTerm: e.target.value,
                    }))
                }
                data-testid="organization-search-input"
            />

            {state.loadError ? (
                <div className="org-no-results" data-testid="organization-search-error">
                    {state.loadError}
                </div>
            ) : state.data.length === 0 &&
                state.searchTerm.length >= ORGANIZATION_SEARCH_MIN_CHARACTERS ? (
                <div className="org-no-results" data-testid="organization-search-empty">
                    No organizations found
                </div>
            ) : (
                <div className="org-results" ref={resultsRef} data-testid="organization-search-results" data-loading={state.isLoading}>
                    {state.data.map((o) => (
                        <button
                            key={o.id}
                            className={`org-option ${o.id === organizationId ? "active" : ""
                                }`}
                            role="option"
                            aria-selected={o.id === organizationId}
                            data-testid={`organization-search-option-${o.id}`}
                            onClick={async () => {
                                try {
                                    await changeOrganization(o);
                                    setShowOrgMenu(false);
                                } catch (error) {
                                    console.error('Failed to change organization:', error);
                                }
                            }}
                        >
                            <span>{o.name}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default React.memo(OrganizationSearcher);
